import { useEffect, useSyncExternalStore } from "react";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { JobDetailDto, JobStatus } from "@workcost/shared";
import { classifyError, EstimateApiError } from "./estimateApi";
import { changeStatus, patchJob, patchMilestone } from "./jobsApi";

/**
 * คิวออฟไลน์สำหรับคำสั่งเล็กๆ ของงานที่บันทึกแล้ว (เปลี่ยนสถานะ และทำเครื่องหมายรับเงินงวด) เก็บใน IndexedDB
 * - เรียงตามเวลาที่สั่ง (seq) และส่งตามลำดับต่อ jobId
 * - ห้ามคิวการลบงานและการบันทึกงาน (ต้องออนไลน์) และไม่คิวคำสั่งที่แนบรูป
 * - server ตรวจกติกาซ้ำเสมอ ถ้า 409 หรือคำสั่งใช้ไม่ได้ จะหยุดคิวของงานนั้นและทิ้งคำสั่งที่เหลือของงานนั้น
 * ไม่เก็บรูปหรือข้อมูลส่วนบุคคลอื่นนอกจากหมายเหตุที่ผู้ใช้พิมพ์เองในคำสั่ง
 */
export type OutboxCmd =
  | { kind: "status"; to: JobStatus; note?: string; dateField?: "actualStart" | "actualFinish"; date?: string }
  | { kind: "milestone_paid"; milestoneId: string; paidAt: string | null };

export type OutboxItem = OutboxCmd & { seq: number; jobId: string; createdAt: number };

interface Schema extends DBSchema {
  cmds: { key: number; value: OutboxItem; indexes: { byJob: string } };
}

const DB_NAME = "workcost-outbox";
let dbPromise: Promise<IDBPDatabase<Schema>> | null = null;

function db(): Promise<IDBPDatabase<Schema>> {
  dbPromise ??= openDB<Schema>(DB_NAME, 1, {
    upgrade(d) {
      const s = d.createObjectStore("cmds", { keyPath: "seq", autoIncrement: true });
      s.createIndex("byJob", "jobId");
    },
  });
  return dbPromise;
}

// ---------- state ที่ UI อ่าน ----------
let items: readonly OutboxItem[] = [];
const subs = new Set<() => void>();
function publish(next: readonly OutboxItem[]): void {
  items = next;
  for (const s of subs) s();
}

/** อ่านคิวทั้งหมดตามลำดับเวลา */
export async function listOutbox(): Promise<OutboxItem[]> {
  try {
    return (await (await db()).getAll("cmds")).sort((a, b) => a.seq - b.seq);
  } catch {
    return [];
  }
}

/** โหลดคิวจาก IndexedDB เข้า state (เรียกตอนเปิดแอป) */
export async function refreshOutbox(): Promise<void> {
  publish(await listOutbox());
}

/** เพิ่มคำสั่งท้ายคิวของงาน คืน false ถ้าเก็บในเครื่องไม่ได้ (ผู้เรียกต้องแจ้งผู้ใช้ว่าไม่สำเร็จ) */
export async function enqueue(jobId: string, cmd: OutboxCmd): Promise<boolean> {
  try {
    const d = await db();
    await d.add("cmds", { ...cmd, jobId, createdAt: Date.now() } as OutboxItem);
    await refreshOutbox();
    return true;
  } catch {
    return false;
  }
}

export async function clearOutbox(): Promise<void> {
  try {
    await (await db()).clear("cmds");
  } catch {
    /* ไม่มีคิวก็ไม่ต้องทำอะไร */
  }
  publish([]);
}

/** สำหรับทดสอบ */
export async function resetOutboxForTests(): Promise<void> {
  if (dbPromise) (await dbPromise).close();
  dbPromise = null;
  items = [];
  running = null;
  rerun = false;
  subs.clear();
}

export function useOutbox(): readonly OutboxItem[] {
  useEffect(() => {
    void refreshOutbox();
  }, []);
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => items,
    () => items,
  );
}

// ---------- ส่งคิว ----------
export type FailureKind = "network" | "retry" | "conflict" | "drop";

/** network/retry: หยุดทั้งคิวแล้วลองใหม่ภายหลัง; conflict/drop: ทิ้งคำสั่งที่เหลือของงานนั้นและแจ้งผู้ใช้ */
export function classifyFailure(e: unknown): FailureKind {
  if (classifyError(e) === "network") return "network";
  if (e instanceof EstimateApiError) {
    if (e.status === 409) return "conflict";
    if (e.status === 401 || e.status === 408 || e.status === 429 || e.status >= 500) return "retry";
    if (e.status >= 400) return "drop";
  }
  return "retry";
}

export interface FlushResult {
  sent: number;
  /** งานที่มีคำสั่งถูกส่ง */
  touched: string[];
  /** งานที่หยุดคิวและทิ้งคำสั่ง */
  dropped: { jobId: string; count: number; kind: "conflict" | "drop" }[];
  /** หยุดกลางทางเพราะออฟไลน์หรือ server ยังไม่พร้อม (คำสั่งที่เหลือยังอยู่ในคิว) */
  stopped?: "network" | "retry";
}

export interface FlushHooks {
  /** เรียกหลังส่งสำเร็จและก่อนเอาคำสั่งออกจากคิว (ใช้เขียน cache ไม่ให้หน้าจอกระพริบกลับสถานะเดิม) */
  onSent?: (item: OutboxItem, result: JobDetailDto | null) => void | Promise<void>;
}

async function send(item: OutboxItem): Promise<JobDetailDto | null> {
  if (item.kind === "status") {
    const updated = await changeStatus(item.jobId, { to: item.to, ...(item.note ? { note: item.note } : {}) });
    if (item.dateField && item.date && updated.dates[item.dateField] !== item.date) {
      try {
        return await patchJob(item.jobId, { [item.dateField]: item.date });
      } catch {
        return updated; // สถานะเปลี่ยนแล้ว วันที่จริงปรับเองได้ที่หน้างาน
      }
    }
    return updated;
  }
  await patchMilestone(item.jobId, item.milestoneId, { paidAt: item.paidAt });
  return null;
}

let running: Promise<FlushResult> | null = null;
let rerun = false;

async function runOnce(hooks: FlushHooks, acc: FlushResult): Promise<void> {
  const blocked = new Set<string>();
  for (;;) {
    const all = await listOutbox();
    const next = all.find((c) => !blocked.has(c.jobId));
    if (!next) return;
    try {
      const result = await send(next);
      await hooks.onSent?.(next, result);
      await (await db()).delete("cmds", next.seq);
      acc.sent += 1;
      if (!acc.touched.includes(next.jobId)) acc.touched.push(next.jobId);
      publish(await listOutbox());
    } catch (e) {
      const kind = classifyFailure(e);
      if (kind === "network" || kind === "retry") {
        acc.stopped = kind;
        return;
      }
      const d = await db();
      const rest = all.filter((c) => c.jobId === next.jobId);
      for (const c of rest) await d.delete("cmds", c.seq);
      acc.dropped.push({ jobId: next.jobId, count: rest.length, kind });
      blocked.add(next.jobId);
      publish(await listOutbox());
    }
  }
}

/** ส่งคำสั่งในคิวตามลำดับ (ทำทีละรอบ ถ้าเรียกซ้อนจะรอรอบเดิมแล้วตรวจคิวอีกครั้ง) */
export function flushOutbox(hooks: FlushHooks = {}): Promise<FlushResult> {
  if (running) {
    rerun = true;
    return running;
  }
  running = (async () => {
    const acc: FlushResult = { sent: 0, touched: [], dropped: [] };
    do {
      rerun = false;
      await runOnce(hooks, acc);
    } while (rerun && !acc.stopped);
    return acc;
  })().finally(() => {
    running = null;
  });
  return running;
}
